import { getPledgePayConfig } from './pledge-pay-config';
import { refreshCsrfToken } from '../shared/csrf-token.js';
import { fullExpiryYear } from '../shared/field-validation.js';
import { reloadPledgesPage } from '../manager/pledges/runtime-registry';
import { readOpenPayRowIds } from './pledge-pay-row-ids';
import { mgrPledgeBillShouldBypassCardValidation } from './pledge-pay-balance';
import {
    getMgrPledgeBillDafCardType,
    mgrPledgeBillValidateNewCard,
} from './pledge-pay-method';
import { collectPledgePaymentRows } from './pledge-pay-rows';
import { buildManagerPledgePaymentPayload } from './pledge-pay-payload';
import { checkoutIntentFor, clearCheckoutIntent } from '../shared/checkout-intent.js';
import { chargeWentThrough } from '../shared/charge-went-through.js';
import { htmlShowsPaymentReceivedPage } from '../shared/payment-received-page.js';
import { updatePledgePayNowButtonEnabledState } from './pledge-pay-amount';
import { showPledgeBillPayErrors, validatePledgeBillPaymentState } from './pledge-pay-validate';
import { isQrPaymentKind, selectedPaymentKind } from '../shared/payment-kind.js';
import { getManagerOverlayElement, showOverlay, hideOverlay } from '../shared/overlay.js';
import {
    getPaymentMode,
    mgrPledgeBillRunAfterFullyCoveredDelay,
    mgrPledgeBillShellDismissIfModal,
    runAfterPledgePaymentModalHidden,
} from './pledge-pay-utils';

const SUBMIT_LOCK_KEY = 'pledgeBillSubmitLocked';
const SUBMIT_GEN_KEY = 'pledgeBillSubmitGeneration';
const PAYMENT_AJAX_TIMEOUT_MS = 120000;
const TOKENIZE_TIMEOUT_MS = 30000;

function showPledgeBillOverlay() {
    showOverlay(getManagerOverlayElement(), { immediate: true });
}

function hidePledgeBillOverlay() {
    hideOverlay(getManagerOverlayElement(), { immediate: true });
}

function payNowButton($payRoot) {
    return $payRoot.find('#pay_now_button');
}

function isPledgeBillSubmitLocked($payRoot) {
    return !!$payRoot.data(SUBMIT_LOCK_KEY);
}

function nextPledgeBillSubmitGeneration($payRoot) {
    const generation = Number($payRoot.data(SUBMIT_GEN_KEY) || 0) + 1;
    $payRoot.data(SUBMIT_GEN_KEY, generation);
    return generation;
}

function beginPledgeBillSubmit($payRoot) {
    if (!$payRoot || !$payRoot.length || isPledgeBillSubmitLocked($payRoot)) {
        return 0;
    }
    const generation = nextPledgeBillSubmitGeneration($payRoot);
    $payRoot.data(SUBMIT_LOCK_KEY, true);
    payNowButton($payRoot).prop('disabled', true);
    return generation;
}

function isCurrentPledgeBillSubmit($payRoot, generation) {
    return isPledgeBillSubmitLocked($payRoot)
        && Number($payRoot.data(SUBMIT_GEN_KEY)) === generation;
}

function releasePledgeBillSubmitLock($payRoot) {
    if (!$payRoot || !$payRoot.length) {
        return;
    }
    $payRoot.data(SUBMIT_LOCK_KEY, false);
    nextPledgeBillSubmitGeneration($payRoot);
    updatePledgePayNowButtonEnabledState($payRoot);
}

function pledgeBillGatewayApiForId(gatewayApisById, gatewayId) {
    const mapped = (gatewayApisById || {})[String(gatewayId)];
    if (!mapped) {
        throw new Error(`pledgeBillGatewayApiForId: no public API key for gateway ${gatewayId}`);
    }
    return String(mapped);
}

function fetchPledgeBillNonce(tokenUrl, gatewayApi, requestBody) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TOKENIZE_TIMEOUT_MS);
    return fetch(tokenUrl, {
        method: 'POST',
        headers: {
            Authorization: 'Basic ' + btoa(gatewayApi + ':'),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
    })
        .then((response) => response.json())
        .then((data) => {
            if (data.nonce_token === undefined) {
                return {
                    ok: false,
                    error: data.error_message || 'Card error',
                };
            }
            return {
                ok: true,
                nonce: data.nonce_token,
            };
        })
        .catch((err) => ({
            ok: false,
            error: err && err.name === 'AbortError'
                ? 'Card tokenization timed out. Please try again.'
                : 'Could not tokenize card.',
        }))
        .finally(() => window.clearTimeout(timer));
}

function collectUniqueGatewayIdsForTokenization($payRoot) {
    const rows = collectPledgePaymentRows($payRoot);
    const vaultForRecurring = getPaymentMode($payRoot) === 'recurring';
    const ids = new Set();
    rows.forEach((row) => {
        const gid = row.gateway_id;
        const amount = parseFloat(String(row.amount ?? '').replace(/,/g, ''));
        const hasChargeAmount = Number.isFinite(amount) && amount > 0;
        if (gid !== undefined && gid !== null && gid !== '' && (vaultForRecurring || hasChargeAmount)) {
            const parsed = parseInt(gid, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
                ids.add(parsed);
            }
        }
    });
    return [...ids];
}

function derivePledgeBillPaymentType($payRoot) {
    const methodPref = selectedPaymentKind($payRoot);
    if (isQrPaymentKind(methodPref)) {
        return 'qr';
    }
    const savedId = String($payRoot.find('#mgr_pledge_bill_saved_payment_id').val() || '').trim();
    return savedId ? 'saved' : 'new';
}

function buildPledgeBillPaymentBasePayload($payRoot, cfg) {
    return {
        ...buildManagerPledgePaymentPayload($payRoot, { csrfToken: cfg.csrfToken }),
        checkout_intent: checkoutIntentFor($payRoot),
        added_by: cfg.pledgeBillPaymentMode === 'member_self' ? 'Member' : 'Manager',
        payment_type: derivePledgeBillPaymentType($payRoot),
    };
}

function buildBillCardPayload($payRoot, extras) {
    const cardVal = String($payRoot.find('#mgr_pledge_bill_card').val() || '');
    return Object.assign({
        payment_type: 'new',
        name: $payRoot.find('#mgr_pledge_bill_name').val(),
        maskedCard: cardVal.replace(/\s/g, '').slice(-4),
        expiryMonth: $payRoot.find('#mgr_pledge_bill_expiry_month').val(),
        expiryYear: fullExpiryYear($payRoot.find('#mgr_pledge_bill_expiry_year').val()),
        cvv2: $payRoot.find('#mgr_pledge_bill_cvv2').val(),
        avs_zip: $payRoot.find('#mgr_pledge_bill_avs_zip').val(),
        use_otp: $payRoot.find('#mgr_pledge_bill_use_otp').prop('checked') ? 'true' : 'false',
        save_card: $payRoot.find('#mgr_pledge_bill_save_card').prop('checked') ? 'true' : 'false',
        daf_card_type: getMgrPledgeBillDafCardType(),
    }, extras || {});
}

function handlePledgeBillPaymentAjaxSuccess(res, $payRoot, cfg) {
    const finish = () => {
        hidePledgeBillOverlay();
        if (!chargeWentThrough(res)) {
            const err = (res && (res.error || res.msg)) ? (res.error || res.msg) : 'Payment failed.';
            showPledgeBillPayErrors($payRoot, [err]);
            return;
        }

        if (res.success) {
            clearCheckoutIntent($payRoot);
        }
        mgrPledgeBillShellDismissIfModal($payRoot);

        const mode = cfg.pledgeBillPaymentMode || 'manager';
        const notify = () => {
            if (!res.success && res.payment_processed) {
                if (mode === 'manager' && res.error) {
                    showAlertMsg(res.error, 'danger');
                }
                return;
            }
            if (htmlShowsPaymentReceivedPage(res.page_info)) {
                return;
            }
            if (res.msg) {
                showAlertMsg(res.msg);
            }
        };

        if (mode === 'manager') {
            runAfterPledgePaymentModalHidden($payRoot, () => reloadPledgesPage(notify, readOpenPayRowIds()));
            return;
        }

        if (res.page_info) {
            $('#index_paging').empty().html(res.page_info).fadeIn('slow');
            notify();
            return;
        }

        openIndexPage();
        notify();
    };

    if (res && res.success && res.fully_covered) {
        mgrPledgeBillRunAfterFullyCoveredDelay(finish);
        return;
    }
    finish();
}

const PLEDGE_BILL_GENERIC_ERROR = 'An unknown error occurred, please try again.';
const PLEDGE_BILL_SESSION_ERROR = 'Your session expired. Refresh the page and sign in again before paying.';

export function pledgeBillPaymentErrorMessage(xhr) {
    const status = xhr && typeof xhr.status === 'number' ? xhr.status : 0;
    if (status === 419 || status === 401) {
        refreshCsrfToken();
        return PLEDGE_BILL_SESSION_ERROR;
    }
    const serverError = xhr && xhr.responseJSON ? xhr.responseJSON.error : '';
    return typeof serverError === 'string' && serverError !== '' ? serverError : PLEDGE_BILL_GENERIC_ERROR;
}

function finishPledgeBillPaymentAjax($payRoot, cfg, payload, complete = null) {
    const config = getPledgePayConfig();
    const billPaymentUrl = cfg.pledgeBillPaymentPostUrl || config.pledgeBillPaymentPostUrl;

    if (!billPaymentUrl) {
        releasePledgeBillSubmitLock($payRoot);
        showPledgeBillPayErrors($payRoot, ['Payment URL is not configured. Please refresh and try again.']);
        return;
    }

    $payRoot.find('#mgr_pledge_bill_cc_ajax_err').hide();
    showPledgeBillOverlay();

    $.ajax({
        url: billPaymentUrl,
        type: 'POST',
        data: payload,
        timeout: PAYMENT_AJAX_TIMEOUT_MS,
        success(res) {
            if (!chargeWentThrough(res)) {
                releasePledgeBillSubmitLock($payRoot);
            }
            if (typeof complete === 'function') {
                complete(chargeWentThrough(res));
            }
            handlePledgeBillPaymentAjaxSuccess(res, $payRoot, cfg);
        },
        error(xhr) {
            hidePledgeBillOverlay();
            releasePledgeBillSubmitLock($payRoot);
            if (typeof complete === 'function') {
                complete(false);
            }
            showPledgeBillPayErrors($payRoot, [pledgeBillPaymentErrorMessage(xhr)]);
        },
        complete(xhr, textStatus) {
            if (textStatus === 'success') {
                return;
            }
            hidePledgeBillOverlay();
        },
    });
}

function submitPledgeBillPayment($payRoot, cfg) {
    if (!$payRoot || !$payRoot.length) {
        return false;
    }

    if (isPledgeBillSubmitLocked($payRoot)) {
        return false;
    }

    if (!validatePledgeBillPaymentState($payRoot)) {
        return false;
    }

    const postData = buildPledgeBillPaymentBasePayload($payRoot, cfg);
    const bypassCard = mgrPledgeBillShouldBypassCardValidation($payRoot);

    if (bypassCard) {
        if (!beginPledgeBillSubmit($payRoot)) {
            return false;
        }
        postData.payment_type = 'manual';
        delete postData.cc_id;
        delete postData.process_type;
        finishPledgeBillPaymentAjax($payRoot, cfg, postData);
        return false;
    }

    const method = postData.payment_type;
    if (method === 'qr') {
        showPledgeBillPayErrors($payRoot, ['Use the QR code button to pay with QR code.']);
        return false;
    }

    if (method === 'saved') {
        postData.cc_id = String($payRoot.find('#mgr_pledge_bill_saved_payment_id').val() || '').trim();
        postData.process_type = 'token';
        if (!postData.cc_id) {
            showPledgeBillPayErrors($payRoot, ['Choose a saved card or switch to pay with a new card.']);
            return false;
        }
        if (!beginPledgeBillSubmit($payRoot)) {
            return false;
        }
        finishPledgeBillPaymentAjax($payRoot, cfg, postData);
        return false;
    }

    const validation = mgrPledgeBillValidateNewCard($payRoot);
    if (!validation.ok) {
        showPledgeBillPayErrors($payRoot, validation.errors || ['Please check the card fields.']);
        return false;
    }

    const config = getPledgePayConfig();
    const tokenUrl = cfg.managerPledgeBillTokenUrl || config.managerPledgeBillTokenUrl || '';
    const gatewayApisById = cfg.managerPledgeBillGatewayApisById || config.managerPledgeBillGatewayApisById || {};
    const dafType = getMgrPledgeBillDafCardType();
    const gatewayIds = collectUniqueGatewayIdsForTokenization($payRoot);
    const tokenizeBody = {
        card: String($payRoot.find('#mgr_pledge_bill_card').val() || '').replace(/ /g, ''),
        cvv2: String($payRoot.find('#mgr_pledge_bill_cvv2').val() || '').replace(/_/g, ''),
    };
    const frozenCardPayload = buildBillCardPayload($payRoot);

    const submitGeneration = beginPledgeBillSubmit($payRoot);
    if (!submitGeneration) {
        return false;
    }

    if (!['pledger', 'donors_fund', 'ojc'].includes(dafType)) {
        if (!tokenUrl) {
            hidePledgeBillOverlay();
            releasePledgeBillSubmitLock($payRoot);
            throw new Error('submitPledgeBillPayment: managerPledgeBillTokenUrl is not configured');
        }
        if (!gatewayIds.length) {
            hidePledgeBillOverlay();
            releasePledgeBillSubmitLock($payRoot);
            throw new Error('submitPledgeBillPayment: no gateway ids available for tokenization');
        }

        showPledgeBillOverlay();
        Promise.all(
            gatewayIds.map((gatewayId) => fetchPledgeBillNonce(
                tokenUrl,
                pledgeBillGatewayApiForId(gatewayApisById, gatewayId),
                tokenizeBody
            ).then((tokenResult) => {
                if (!tokenResult.ok) {
                    throw new Error(tokenResult.error);
                }
                return [String(gatewayId), tokenResult.nonce];
            }))
        )
            .then((tokenResults) => {
                if (!isCurrentPledgeBillSubmit($payRoot, submitGeneration)) {
                    return;
                }
                finishPledgeBillPaymentAjax(
                    $payRoot,
                    cfg,
                    Object.assign({}, postData, frozenCardPayload, {
                        process_type: 'nonce',
                        nonces: Object.fromEntries(tokenResults),
                    })
                );
            })
            .catch((err) => {
                if (!isCurrentPledgeBillSubmit($payRoot, submitGeneration)) {
                    return;
                }
                hidePledgeBillOverlay();
                releasePledgeBillSubmitLock($payRoot);
                showPledgeBillPayErrors($payRoot, [err.message || 'Could not tokenize this card.']);
            });
        return false;
    }

    finishPledgeBillPaymentAjax(
        $payRoot,
        cfg,
        Object.assign({}, postData, frozenCardPayload, {
            process_type: 'direct',
            nonce: '',
            daf_card: tokenizeBody.card,
        })
    );
    return false;
}

function submitPledgeSwipePayment($payRoot, cfg, swipeResult, gatewayId) {
    if (!$payRoot || !$payRoot.length || isPledgeBillSubmitLocked($payRoot) || !validatePledgeBillPaymentState($payRoot)) {
        return false;
    }

    if (!swipeResult?.nonce) {
        showPledgeBillPayErrors($payRoot, ['The swiped card could not be read. Please try again.']);
        return false;
    }

    if (!beginPledgeBillSubmit($payRoot)) {
        return false;
    }

    finishPledgeBillPaymentAjax($payRoot, cfg, Object.assign(buildPledgeBillPaymentBasePayload($payRoot, cfg), {
        payment_type: 'new',
        process_type: 'nonce',
        type: 'swipe',
        is_swipe: true,
        nonces: { [String(gatewayId)]: swipeResult.nonce },
        name: swipeResult.name,
        maskedCard: swipeResult.maskedCard,
        cardType: swipeResult.cardType,
        last4: swipeResult.last4,
        expiryMonth: swipeResult.expiryMonth,
        expiryYear: swipeResult.expiryYear,
    }));
    return false;
}

function submitPledgeWalletPayment($payRoot, cfg, tokenData, complete = null) {
    if (!$payRoot || !$payRoot.length || isPledgeBillSubmitLocked($payRoot) || !validatePledgeBillPaymentState($payRoot)) {
        if (typeof complete === 'function') {
            complete(false);
        }
        return false;
    }

    const walletSource = String(tokenData?.wallet_source || '');
    const paymentKey = tokenData?.payment_key;
    if (!['googlepay', 'applepay'].includes(walletSource) || !paymentKey) {
        showPledgeBillPayErrors($payRoot, ['The wallet payment token is missing. Please try again.']);
        if (typeof complete === 'function') {
            complete(false);
        }
        return false;
    }

    if (!beginPledgeBillSubmit($payRoot)) {
        if (typeof complete === 'function') {
            complete(false);
        }
        return false;
    }

    const payload = Object.assign(buildPledgeBillPaymentBasePayload($payRoot, cfg), {
        payment_type: 'new',
        process_type: walletSource,
        wallet_source: walletSource,
        payment_key: paymentKey,
    });
    if (tokenData.bin_type) {
        payload.bin_type = tokenData.bin_type;
    }

    finishPledgeBillPaymentAjax($payRoot, cfg, payload, complete);
    return false;
}

export {
    beginPledgeBillSubmit,
    buildPledgeBillPaymentBasePayload,
    isPledgeBillSubmitLocked,
    releasePledgeBillSubmitLock,
    submitPledgeBillPayment,
    submitPledgeSwipePayment,
    submitPledgeWalletPayment,
};
